// src/features/system/devices/api/iotDevicesApi.ts
// ────────────────────────────────────────────────────────────────────────────
// Typed API client for IoT device management.
//
// Endpoints map to:
//   /bff/device-ops/*                — device CRUD, config, presence, quarantine
//   /bff/orchestrator/hot-stream/*   — telemetry queries (snapshot, history)
//
// Types mirror the BFF service.mjs digestDevice / digestConfig shapes
// and the orchestrator hot_stream contract.
// ────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

// ── Domain types ─────────────────────────────────────────────────────────

/** Device entity as returned by BFF device-ops (scope-based, null-stripped). */
export interface Device {
  device_id:      string;
  id_scope:       string;
  label?:         string;
  is_active:      boolean;
  tenant_id?:     number;         // authoritative, from workflow.devices
  authorized_via?: string;
  device_type?:   string;         // 'pacdaemon' | 'esp32' | 'shelly' | …
  user_agent?:    string;
  origin?:        string;
  platform?:      string;
  language?:      string;
  screen?:        string;
  remote_ip?:     string;
  approved_by?:   string;
  approved_at?:   string;
  scope_granted?: string;
  last_seen_at?:  string;
  fingerprint?:   string;
  created_at?:    string;
}

/** Device + current config attached (from GET /devices/:id). */
export interface DeviceWithConfig extends Device {
  current_config: DeviceConfig | null;
}

/** Device config entity (scope-based, child of device.instance). */
export interface DeviceConfig {
  id_scope:   string;
  device_id:  string;
  revision:   number;
  is_active:  boolean;
  config:     Record<string, unknown>;
  sha256:     string;
  created_at: string | null;
}

/** Presence entry returned by GET /presence. */
export interface DevicePresence {
  device_id:  string;
  online:     boolean;
  last_seen:  string | null;
  ip?:        string | null;
  label?:     string | null;
}

/** Pending device awaiting admin approval. */
export interface PendingDevice {
  device_id:  string;
  id_scope:   string;
  first_seen: string | null;
  updated_at: string | null;
}

/** Blacklisted device. */
export interface BlacklistedDevice {
  device_id:   string;
  id_scope:    string;
  label?:      string | null;
  reason?:     string | null;
  rejected_at: string | null;
  updated_at:  string | null;
}

/** Label history entry from HotStream device.label channel. */
export interface LabelHistoryEntry {
  label:      string | null;
  old_label:  string | null;
  context:    'approval' | 'rename' | 'initial' | string;
  changed_at: string;
}

/** Blacklist label history entry from HotStream blacklist.label channel. */
export interface BlacklistLabelHistoryEntry {
  label:      string | null;
  context:    'rejection' | 'blacklist' | string;
  changed_at: string;
}

/** IoT telemetry snapshot row (pivot view — one row per device). */
export interface TelemetrySnapshot {
  scope_key:   string;              // device_id
  temperature: number | null;
  humidity:    number | null;
  battery:     number | null;
  rssi:        number | null;
  power:       number | null;
  last_ts:     string | null;       // latest reading timestamp
}

/** Hot-stream history row (raw time-series). */
export interface TelemetryHistoryRow {
  id:           number;
  scope_ns:     string;
  scope_key:    string;             // device_id
  id_category:  string;
  id_type:      string;
  v_numeric:    number | null;
  v_text:       string | null;
  v_jsonb:      Record<string, unknown> | null;
  created_at:   string;
}

/** Device log entry (from hot_stream, category=3, scope_ns=iot.device.log). */
export interface DeviceLogEntry {
  id:         number;
  scope_key:  string;               // device_id
  level:      string;               // TRACE | DEBUG | INFO | WARN | ERROR
  tag:        string;
  msg:        string;
  device_ts_ms: number | null;      // millis() on device at log time
  created_at: string;               // server ingestion timestamp
}

/** Payload for registering / updating a device. */
export interface RegisterDeviceRequest {
  device_id:   string;
  label?:      string;
  is_active?:  boolean;
  device_type?: string;
}

/** Payload for writing device config. */
export interface WriteDeviceConfigRequest {
  config:     Record<string, unknown>;
  is_active?: boolean;
}

// ── Query keys (for TanStack Query cache) ────────────────────────────────

export const DEVICE_KEYS = {
  list:       ['devices', 'iot', 'list']       as const,
  detail:     (id: string) => ['devices', 'iot', 'detail', id] as const,
  presence:   ['devices', 'iot', 'presence']   as const,
  pending:    ['devices', 'iot', 'pending']    as const,
  blacklist:  ['devices', 'iot', 'blacklist']  as const,
  snapshot:   ['devices', 'iot', 'snapshot']   as const,
  viewLinks:  ['devices', 'iot', 'viewLinks']  as const,
  labelHistory:     (id: string) => ['devices', 'iot', 'labelHistory', id] as const,
  blLabelHistory:   (id: string) => ['devices', 'iot', 'blLabelHistory', id] as const,
  history:    (deviceId: string) => ['devices', 'iot', 'history', deviceId] as const,
  logs:       (deviceId: string) => ['devices', 'iot', 'logs', deviceId] as const,
  stats:      (windowLabel: string) => ['devices', 'iot', 'stats', windowLabel] as const,
} as const;

// ── API base paths ───────────────────────────────────────────────────────

const DEV_OPS  = '/device-ops';
const HOT      = '/orchestrator/hot-stream';

// ── Device CRUD ──────────────────────────────────────────────────────────

export const fetchDevicesApi = async (): Promise<Device[]> => {
  const res = await bff.get<{ items: Device[] }>(`${DEV_OPS}/devices`);
  return Array.isArray(res?.items) ? res.items : [];
};

export const fetchDeviceApi = async (deviceId: string): Promise<DeviceWithConfig | null> => {
  return bff.get<DeviceWithConfig>(`${DEV_OPS}/devices/${encodeURIComponent(deviceId)}`);
};

export const registerDeviceApi = (body: RegisterDeviceRequest) =>
  bff.post<Device>(`${DEV_OPS}/devices`, body);

export const updateDeviceLabelApi = (deviceId: string, label: string) =>
  bff.post<Device>(`${DEV_OPS}/devices`, { device_id: deviceId, label });

/** Rename a device label via dedicated PATCH endpoint (writes hot-stream audit event). */
export const renameDeviceApi = (deviceId: string, label: string) =>
  bff.patch<Device>(
    `${DEV_OPS}/devices/${encodeURIComponent(deviceId)}/label`,
    { label },
  );

export const revokeDeviceApi = (deviceId: string) =>
  bff.post(`${DEV_OPS}/devices/${encodeURIComponent(deviceId)}/revoke`, {});

export const blacklistDeviceApi = (deviceId: string, reason?: string, label?: string) =>
  bff.post(`${DEV_OPS}/devices/${encodeURIComponent(deviceId)}/blacklist`, { reason, label });

/** Reassign a device to a different tenant (admin). */
export const updateDeviceTenantApi = (deviceId: string, tenantId: number) =>
  bff.patch<Device>(`${DEV_OPS}/devices/${encodeURIComponent(deviceId)}/tenant`, { tenant_id: tenantId });

// ── Config ───────────────────────────────────────────────────────────────

export const fetchDeviceConfigApi = async (deviceId: string): Promise<DeviceConfig | null> =>
  bff.get<DeviceConfig>(`${DEV_OPS}/devices/${encodeURIComponent(deviceId)}/config`);

export const writeDeviceConfigApi = (deviceId: string, body: WriteDeviceConfigRequest) =>
  bff.post(`${DEV_OPS}/devices/${encodeURIComponent(deviceId)}/config`, body);

// ── Presence ─────────────────────────────────────────────────────────────

export const fetchPresenceApi = async (): Promise<DevicePresence[]> => {
  const res = await bff.get<Record<string, DevicePresence>>(`${DEV_OPS}/presence`);
  if (!res || typeof res !== 'object') return [];
  return Object.values(res);
};

export const fetchDevicePresenceApi = async (deviceId: string): Promise<DevicePresence | null> =>
  bff.get<DevicePresence>(`${DEV_OPS}/presence/${encodeURIComponent(deviceId)}`);

// ── Quarantine (pending / approve / reject / blacklist / unblock) ────────

export const fetchPendingDevicesApi = async (): Promise<PendingDevice[]> => {
  const res = await bff.get<{ items: PendingDevice[] }>(`${DEV_OPS}/iot/pending`);
  return Array.isArray(res?.items) ? res.items : [];
};

export const approveDeviceApi = (deviceId: string, label?: string) =>
  bff.post(`${DEV_OPS}/iot/approve`, { device_id: deviceId, label });

export const rejectDeviceApi = (deviceId: string, reason?: string, label?: string) =>
  bff.post(`${DEV_OPS}/iot/reject`, { device_id: deviceId, reason, label });

export const fetchBlacklistedDevicesApi = async (): Promise<BlacklistedDevice[]> => {
  const res = await bff.get<{ items: BlacklistedDevice[] }>(`${DEV_OPS}/iot/blacklist`);
  return Array.isArray(res?.items) ? res.items : [];
};

export const unblockDeviceApi = (deviceId: string) =>
  bff.post(`${DEV_OPS}/iot/unblock`, { device_id: deviceId });

// ── Label History (HotStream channels) ────────────────────────────────

/** Fetch the full label rename history for a device (newest → oldest). */
export const fetchLabelHistoryApi = async (deviceId: string): Promise<LabelHistoryEntry[]> => {
  const res = await bff.get<{ items: LabelHistoryEntry[] }>(
    `${DEV_OPS}/devices/${encodeURIComponent(deviceId)}/label/history`,
  );
  return Array.isArray(res?.items) ? res.items : [];
};

/** Fetch the blacklist label history for a device (newest → oldest). */
export const fetchBlacklistLabelHistoryApi = async (deviceId: string): Promise<BlacklistLabelHistoryEntry[]> => {
  const res = await bff.get<{ items: BlacklistLabelHistoryEntry[] }>(
    `${DEV_OPS}/iot/blacklist/${encodeURIComponent(deviceId)}/label/history`,
  );
  return Array.isArray(res?.items) ? res.items : [];
};

// ── Telemetry (hot-stream) ───────────────────────────────────────────────

export const fetchTelemetrySnapshotApi = async (): Promise<TelemetrySnapshot[]> => {
  const res = await bff.get<{ items: TelemetrySnapshot[] }>(`${HOT}/snapshot`);
  return Array.isArray(res?.items) ? res.items : [];
};

export interface TelemetryHistoryQuery {
  scope_key:    string;             // device_id
  scope_ns?:    string;             // default 'iot'
  id_category?: string;
  id_type?:     string;
  from?:        string;             // ISO timestamp
  to?:          string;
  limit?:       number;
}

export const fetchTelemetryHistoryApi = async (q: TelemetryHistoryQuery): Promise<TelemetryHistoryRow[]> => {
  const params = new URLSearchParams();
  params.set('scope_key', q.scope_key);
  params.set('scope_ns', q.scope_ns ?? 'iot');
  if (q.id_category) params.set('id_category', q.id_category);
  if (q.id_type)     params.set('id_type', q.id_type);
  if (q.from)        params.set('from', q.from);
  if (q.to)          params.set('to', q.to);
  if (q.limit)       params.set('limit', String(q.limit));
  const res = await bff.get<{ items: TelemetryHistoryRow[] }>(`${HOT}/history?${params}`);
  return Array.isArray(res?.items) ? res.items : [];
};

// ── IoT Telemetry Series (digested by BFF) ───────────────────────────────

/** A single time-series point for a metric (epoch-ms + numeric value). */
export interface TelemetrySeriesPoint {
  ts:    number;   // epoch-ms
  value: number;
}

/** Human-readable table row (produced by BFF, no raw v_xxx). */
export interface TelemetryTableRow {
  ts:     string;  // ISO timestamp
  metric: string;  // "Temperature" | "Humidity" | …
  value:  number;
  unit:   string;  // "°C" | "%" | "dBm" | …
}

/** Pre-shaped IoT telemetry response from BFF /iot-telemetry. */
export interface IoTTelemetrySeries {
  temp:  TelemetrySeriesPoint[];
  hum:   TelemetrySeriesPoint[];
  rssi:  TelemetrySeriesPoint[];
  table: TelemetryTableRow[];
}

export interface IoTTelemetrySeriesQuery {
  scope_key: string;
  scope_ns?: string;
  limit?:    number;
  from?:     string;
  to?:       string;
}

/** Fetch pre-digested IoT telemetry series (BFF shapes it for FE). */
export const fetchIoTTelemetrySeriesApi = async (q: IoTTelemetrySeriesQuery): Promise<IoTTelemetrySeries> => {
  const params = new URLSearchParams();
  params.set('scope_key', q.scope_key);
  params.set('scope_ns', q.scope_ns ?? 'iot');
  if (q.limit) params.set('limit', String(q.limit));
  if (q.from)  params.set('from', q.from);
  if (q.to)    params.set('to', q.to);
  const res = await bff.get<IoTTelemetrySeries>(`${HOT}/iot-telemetry?${params}`);
  return {
    temp:  Array.isArray(res?.temp)  ? res.temp  : [],
    hum:   Array.isArray(res?.hum)   ? res.hum   : [],
    rssi:  Array.isArray(res?.rssi)  ? res.rssi  : [],
    table: Array.isArray(res?.table) ? res.table : [],
  };
};

// ── Device Telemetry Stats (BFF-digested, no raw IDs) ────────────────────

/** Per-metric count breakdown (produced by BFF). */
export interface MetricStat {
  metric: string;   // "Temperature" | "Humidity" | "RSSI"
  unit:   string;   // "°C" | "%RH" | "dBm"
  count:  number;
}

/** Per-device ingestion statistics (produced by BFF). */
export interface DeviceStat {
  deviceKey:         string;        // scope_key — FE maps to label
  totalRequests:     number;
  telemetryCount:    number;
  eventCount:        number;
  statusCount:       number;
  diagnosticCount:   number;
  firstSeen:         string;        // ISO timestamp
  lastSeen:          string;        // ISO timestamp
  avgIntervalSec:    number | null;
  requestsPerMinute: number | null;
  spanSec:           number;
  byMetric:          MetricStat[];
}

/** Fleet-level telemetry stats response. */
export interface DeviceStatsResponse {
  devices:         DeviceStat[];
  fleetTotal:      number;
  fleetTelemetry:  number;
  fleetEvents:     number;
  fleetStatus:     number;
  fleetDiagnostic: number;
}

export interface DeviceStatsQuery {
  scope_ns?: string;
  from?:     string;
  to?:       string;
  limit?:    number;
}

/** Fetch BFF-digested per-device ingestion statistics. */
export const fetchDeviceStatsApi = async (q: DeviceStatsQuery = {}): Promise<DeviceStatsResponse> => {
  const params = new URLSearchParams();
  if (q.scope_ns) params.set('scope_ns', q.scope_ns);
  if (q.from)     params.set('from', q.from);
  if (q.to)       params.set('to', q.to);
  if (q.limit)    params.set('limit', String(q.limit));
  const qs = params.toString();
  const res = await bff.get<DeviceStatsResponse>(`${HOT}/iot-telemetry/stats${qs ? `?${qs}` : ''}`);
  return {
    devices:         Array.isArray(res?.devices) ? res.devices : [],
    fleetTotal:      res?.fleetTotal ?? 0,
    fleetTelemetry:  res?.fleetTelemetry ?? 0,
    fleetEvents:     res?.fleetEvents ?? 0,
    fleetStatus:     res?.fleetStatus ?? 0,
    fleetDiagnostic: res?.fleetDiagnostic ?? 0,
  };
};

// ── Device Logs (hot-stream, category=3, scope_ns=iot.device.log) ────────

export interface DeviceLogQuery {
  device_id: string;
  level?:    string;    // filter by level (INFO, WARN, ERROR, etc.)
  from?:     string;    // ISO timestamp
  to?:       string;
  limit?:    number;
}

/**
 * Fetch device log entries from hot-stream.
 * Queries with scope_ns=iot.device.log, id_category=3 and pivots
 * the v_jsonb into typed DeviceLogEntry objects.
 */
export const fetchDeviceLogsApi = async (q: DeviceLogQuery): Promise<DeviceLogEntry[]> => {
  const params = new URLSearchParams();
  params.set('scope_key', q.device_id);
  params.set('scope_ns', 'iot.device.log');
  params.set('id_category', '3');
  if (q.from)  params.set('from', q.from);
  if (q.to)    params.set('to', q.to);
  if (q.limit) params.set('limit', String(q.limit));

  const res = await bff.get<{ items: TelemetryHistoryRow[] }>(`${HOT}/history?${params}`);
  const raw = Array.isArray(res?.items) ? res.items : [];

  return raw.map(r => {
    const j = (r.v_jsonb ?? {}) as Record<string, unknown>;
    return {
      id:           r.id,
      scope_key:    r.scope_key,
      level:        (j.level as string) ?? 'INFO',
      tag:          (j.tag as string) ?? '',
      msg:          (j.msg as string) ?? r.v_text ?? '',
      device_ts_ms: typeof j.device_ts_ms === 'number' ? j.device_ts_ms : null,
      created_at:   r.created_at,
    };
  }).filter(e => !q.level || e.level.toUpperCase().startsWith(q.level.toUpperCase()));
};

// ── IoT-enriched types (ESP32 device view) ───────────────────────────────

/**
 * Enriched IoT device view — extends the base Device identity with
 * firmware + hardware metadata + config fields that ESP32 devices report.
 *
 * Fields are nullable because they populate progressively as the device
 * checks in and the BFF joins config data into the response.
 */
export interface IoTDevice extends Device {
  hw_id:             string;
  chip_model:        string | null;
  chip_revision:     number | null;
  flash_kb:          number | null;
  fw_name:           string | null;
  fw_version:        string | null;
  fw_build_date:     string | null;
  endpoint_url:      string | null;
  publish_period_ms: number | null;
  wifi_network_count: number;
  wifi_enabled:      boolean;
}

/** Config push payload for IoT device firmware ConfigStore. */
export interface IoTDeviceConfigUpdate {
  endpoint_url?:      string;
  publish_period_ms?: number;
  wifi_networks?:     { ssid: string; pass: string }[];
  wifi_enabled?:      boolean;
}

/** Individual telemetry reading (pivoted from hot-stream rows). */
export interface TelemetryReading {
  recorded_at: string;
  temperature: number | null;
  humidity:    number | null;
  rssi:        number | null;
  heap_free:   number | null;
  uptime_s:    number | null;
}

// ── IoT-enriched fetchers ────────────────────────────────────────────────

/**
 * Fetch all devices typed as IoTDevice (superset of Device).
 * Extra fields populate when the BFF joins config + firmware metadata.
 */
export const fetchIoTDevicesApi = async (): Promise<IoTDevice[]> => {
  const res = await bff.get<{ items: IoTDevice[] }>(`${DEV_OPS}/devices`);
  return Array.isArray(res?.items) ? res.items : [];
};

/** Fetch a single IoT device by ID (enriched view). */
export const fetchIoTDeviceApi = async (deviceId: string): Promise<IoTDevice | null> =>
  bff.get<IoTDevice>(`${DEV_OPS}/devices/${encodeURIComponent(deviceId)}`);

/** Push a config update to an IoT device's firmware ConfigStore. */
export const pushIoTDeviceConfigApi = (deviceId: string, update: IoTDeviceConfigUpdate) =>
  writeDeviceConfigApi(deviceId, { config: update as Record<string, unknown> });

/** Params for the telemetry fetch. */
export interface FetchTelemetryParams {
  device_id: string;
  limit?:    number;
}

/**
 * Fetch telemetry readings for a device.
 * Wraps the hot-stream history endpoint and pivots raw rows into
 * `TelemetryReading` objects (one per timestamp bucket).
 */
export const fetchTelemetryApi = async (params: FetchTelemetryParams): Promise<TelemetryReading[]> => {
  const rows = await fetchTelemetryHistoryApi({
    scope_key: params.device_id,
    scope_ns:  'iot',
    limit:     params.limit,
  });
  return pivotHistoryToReadings(rows);
};

// ── Internal helpers ─────────────────────────────────────────────────────

/** Pivot raw hot-stream rows into TelemetryReading objects. */
function pivotHistoryToReadings(rows: TelemetryHistoryRow[]): TelemetryReading[] {
  const buckets = new Map<string, TelemetryReading>();
  for (const r of rows) {
    const ts = r.created_at;
    if (!buckets.has(ts)) {
      buckets.set(ts, {
        recorded_at: ts, temperature: null, humidity: null,
        rssi: null, heap_free: null, uptime_s: null,
      });
    }
    const b = buckets.get(ts)!;
    const k = `${r.id_category}.${r.id_type}`;
    if (k.includes('temperature'))       b.temperature = r.v_numeric;
    else if (k.includes('humidity'))     b.humidity    = r.v_numeric;
    else if (k.includes('rssi'))         b.rssi        = r.v_numeric;
    else if (k.includes('heap'))         b.heap_free   = r.v_numeric;
    else if (k.includes('uptime'))       b.uptime_s    = r.v_numeric;
  }
  return Array.from(buckets.values()).sort((a, b) => b.recorded_at.localeCompare(a.recorded_at));
}

// ── Shared View Links ────────────────────────────────────────────────────

/** Device entry with optional label override for a shared view link. */
export interface ViewLinkDevice {
  device_id: string;
  label?:    string | null;
}

/** Shared view link entity (BFF-digested). */
export interface ViewLink {
  token:       string;
  label?:      string;
  devices:     ViewLinkDevice[];
  time_window?: string;
  created_by?: string;
  created_at?: string;
  is_active:   boolean;
  url_path:    string;
}

/** Payload for creating a shared view link. */
export interface CreateViewLinkRequest {
  devices:      ViewLinkDevice[];
  label?:       string;
  time_window?: string;
}

/** Create a shared view link and return the generated URL path. */
export const createViewLinkApi = (body: CreateViewLinkRequest): Promise<ViewLink> =>
  bff.post<ViewLink>(`${DEV_OPS}/views`, body);

/** Fetch a single view link by token (public — no auth required). */
export const fetchViewLinkApi = (token: string): Promise<ViewLink | null> =>
  bff.get<ViewLink>(`${DEV_OPS}/views/${encodeURIComponent(token)}`);

/** List all view links for the current session. */
export const fetchViewLinksApi = async (): Promise<ViewLink[]> => {
  const res = await bff.get<{ items: ViewLink[] }>(`${DEV_OPS}/views`);
  return Array.isArray(res?.items) ? res.items : [];
};

/** Deactivate (soft-delete) a shared view link. */
export const deactivateViewLinkApi = (token: string): Promise<ViewLink> =>
  bff.del<ViewLink>(`${DEV_OPS}/views/${encodeURIComponent(token)}`);
